import React from "react";
import { Param, ParamTable } from "./Paramtable";
import { EndpointHeader } from "./Endpointheader";
import { MethodBadge } from "./Methodbadge";
import { CodeBlock } from "./Codeblock";

// ─── Params ───────────────────────────────────────────────────────────────

const QUERY_PARAMS: Param[] = [
    {
        name: "page",
        type: "number",
        required: true,
        description: "Page index (starts from 0).",
    },
    {
        name: "size",
        type: "number",
        required: true,
        description: "Number of records per page.",
    },
];

const BODY_PARAMS: Param[] = [
    {
        name: "userId",
        type: "string",
        required: true,
        description: "User ID for which messages are fetched.",
    },
    {
        name: "toNumber",
        type: "string",
        required: false,
        description: "Filter by recipient phone number.",
    },
    {
        name: "templateName",
        type: "string",
        required: false,
        description: "Filter by template name.",
    },
    {
        name: "status",
        type: "string",
        required: false,
        description: "Filter by message status (SENT, FAILED, etc).",
    },
];

// ─── Static Code Blocks ───────────────────────────────────────────────────

const REQUEST_PAYLOAD = `{
  "userId": "1",
  "companyId": "5",
  "toNumber": "",
  "templateName": "",
  "status": ""
}`;

const CURL = `curl 'https://apiwhat.empedance.com/api/whatsapp/company/send/whatsapp/messages/search?page=0&size=10' \\
  -H 'Authorization: Bearer <your_token>' \\
  -H 'Content-Type: application/json' \\
  --data-raw '{
  "userId": 1,
  "companyId": 5,
  "toNumber": "",
  "templateName": "",
  "status": "",
  "deliveryStatus": "",
  "waMessageId": ""
}'`;

const RESPONSE_200 = `{
  "status": 1,
  "data": {
    "results": [
      {
        "id": 671,
        "userId": 1,
        "toNumber": "917351166989",
        "templateName": "text_header_template",
        "language": "en_US",
        "status": "SENT",
        "waMessageId": "wamid.xxx",
        "deliveryStatus": "READ",
        "sentAt": 1776235029936
      }
    ],
    "page": {
      "index": 0,
      "size": 10,
      "total": 585,
      "count": 59
    }
  },
  "error": null
}`;

// ─── Main Component ───────────────────────────────────────────────────────

export const MessageReportSection: React.FC = () => {
    return (
        <div>
            {/* Title */}
            <div className="flex items-center gap-3 mb-2">
                <MethodBadge method="POST" />
                <h1 className="text-2xl font-bold text-gray-900">
                    Message Report
                </h1>
            </div>

            <p className="text-gray-500 text-sm mt-1">
                Search and filter WhatsApp message logs with pagination support.
            </p>

            {/* Endpoint */}
            <EndpointHeader
                method="POST"
                url="/api/whatsapp/company/send/whatsapp/messages/search"
            />

            {/* Query Params */}
            <ParamTable title="Query Parameters" params={QUERY_PARAMS} />

            {/* Body Params */}
            <ParamTable title="Request Body" params={BODY_PARAMS} />

            {/* Filters Note */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
                <span className="font-semibold">🔍 Filtering:</span> All fields except{" "}
                <code className="bg-blue-100 px-1 rounded">userId</code> are optional.
                Leave them empty to fetch all messages.
            </div>

            {/* Code Blocks */}
            <CodeBlock
                title="Request Example"
                variants={[
                    { label: "JSON", value: REQUEST_PAYLOAD },
                    { label: "cURL", value: CURL },
                ]}
            />

            <CodeBlock
                title="RESPONSE 200 · OK"
                variants={[{ label: "JSON", value: RESPONSE_200 }]}
            />

            {/* Response Notes */}
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700">
                <p className="font-semibold mb-1">📊 Response Fields:</p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                    <li><b>status</b>: API status (1 = success)</li>
                    <li><b>results</b>: Array of message records</li>
                    <li><b>deliveryStatus</b>: SENT, DELIVERED, READ, FAILED</li>
                    <li><b>sentAt</b>: Timestamp (milliseconds)</li>
                    <li><b>page</b>: Pagination metadata</li>
                </ul>
            </div>
        </div>
    );
};